import { Card, Chip, Divider, Grid, styled, Typography } from "@mui/material";
import { blue, green, orange, pink, purple } from "@mui/material/colors";
import { Curriculum } from "api/types";
import clsx from "clsx";
import { PreviewCurriculum } from "components/PreviewCurriculum";

const PREFIX = "CurriculumCard";

const classes = {
	root: `${PREFIX}-root`,
	chip: `${PREFIX}-chip`,
	section1: `${PREFIX}-section1`,
	section2: `${PREFIX}-section2`,
	section3: `${PREFIX}-section3`,
	previewBtn: `${PREFIX}-previewBtn`,
	quiz: `${PREFIX}-quiz`,
	animation: `${PREFIX}-animation`,
	game: `${PREFIX}-game`,
	book: `${PREFIX}-book`,
	song: `${PREFIX}-song`,
};

const StyledGrid = styled(Grid)(({ theme }) => ({
	minWidth: "250px",
	borderRadius: 4,

	[`&.${classes.root}`]: {
		padding: theme.spacing(1),
		minHeight: "300px",
	},

	[`& .${classes.chip}`]: {
		margin: theme.spacing(0.5),
		backgroundColor: theme.palette.primary.main,
		color: theme.palette.primary.contrastText,
	},

	[`& .${classes.section1}`]: {
		margin: theme.spacing(0.5),
		marginBottom: 0,
	},

	[`& .${classes.section2}`]: {
		margin: theme.spacing(2),
		width: "auto",
	},

	[`& .${classes.section3}`]: {
		padding: theme.spacing(1),
		maxHeight: "min-content",
	},
	[`& .${classes.song}`]: {
		backgroundColor: purple[400],
	},
	[`& .${classes.game}`]: {
		backgroundColor: orange[400],
	},
	[`& .${classes.quiz}`]: {
		backgroundColor: green[400],
	},
	[`& .${classes.animation}`]: {
		backgroundColor: blue[400],
	},
	[`& .${classes.book}`]: {
		backgroundColor: pink[400],
	},
}));

export interface CurriculumCardProps {
	curriculum: Curriculum;
}

export const CurriculumCard = ({
	curriculum,
}: CurriculumCardProps): JSX.Element => {
	const hashCode = (str: string): number => {
		let h: number = 0;
		for (let i = 0; i < str.length; i++) {
			h = 31 * h + str.charCodeAt(i);
		}
		return h & 0xffffffff;
	};

	return (
		<Card>
			<StyledGrid
				container
				justifyContent="space-between"
				direction="column"
				className={classes.root}
			>
				<Grid item className={classes.section1}>
					<Grid
						container
						alignItems="flex-start"
						justifyContent="space-between"
					>
						<Grid item xs={7}>
							<Typography gutterBottom variant="h5">
								{curriculum.name}
							</Typography>
						</Grid>
						<Grid item>
							<PreviewCurriculum curriculum={curriculum} />
						</Grid>
					</Grid>
					<Typography variant="subtitle1" style={{ fontWeight: 600 }}>
						{curriculum.strand}
					</Typography>
					<Typography variant="body1" sx={{ my: 1.5 }}>
						{curriculum.description}
					</Typography>
				</Grid>
				<Grid>
					<Divider variant="middle" />
					<Grid
						container
						justifyContent="space-between"
						className={classes.section2}
					>
						<Chip
							label={curriculum.learningArea}
							className={clsx({
								[classes.animation]:
									hashCode(curriculum.learningArea) % 5 === 0,
								[classes.game]: hashCode(curriculum.learningArea) % 5 === 1,
								[classes.book]: hashCode(curriculum.learningArea) % 5 === 2,
								[classes.song]: hashCode(curriculum.learningArea) % 5 === 3,
								[classes.quiz]: hashCode(curriculum.learningArea) % 5 === 4,
								[classes.chip]: curriculum.learningArea,
							})}
							// className={classes.animation}
						/>
						<Typography variant="subtitle2" sx={{ my: "auto" }}>
							{curriculum.code}
						</Typography>
						<Typography variant="subtitle2" sx={{ my: "auto" }}>
							{curriculum.yearLevel}
						</Typography>
					</Grid>
				</Grid>
			</StyledGrid>
		</Card>
	);
};
